import _assign from "object-assign";

function _defineProperty(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }

function _objectWithoutProperties(source, excluded) { if (source == null) return {}; var target = _objectWithoutPropertiesLoose(source, excluded); var key, i; if (Object.getOwnPropertySymbols) { var sourceSymbolKeys = Object.getOwnPropertySymbols(source); for (i = 0; i < sourceSymbolKeys.length; i++) { key = sourceSymbolKeys[i]; if (excluded.indexOf(key) >= 0) continue; if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue; target[key] = source[key]; } } return target; }

function _objectWithoutPropertiesLoose(source, excluded) { if (source == null) return {}; var target = {}; var sourceKeys = Object.keys(source); var key, i; for (i = 0; i < sourceKeys.length; i++) { key = sourceKeys[i]; if (excluded.indexOf(key) >= 0) continue; target[key] = source[key]; } return target; }

import { withStyles } from '@jpmuitk/theme';
import { ListBase, ListStateContext } from '@jpmuitk/list';
import React, { forwardRef, useContext } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { refType } from '@material-ui/utils';
import { useForkRef } from '@material-ui/core/utils';
import Popper from '@jpmuitk/popper';
import CustomListContext from './custom-elements/CustomListContext';
import DropdownButton from './DropdownButton';
import { isObjectEmpty } from './internal/isObjectEmpty';
import { useDropdown } from './useDropdown';
import { style } from './style';
/**
 * Renders a multi-select dropdown with selectable items
 */

var MultiSelectDropdown = /*#__PURE__*/forwardRef(function MultiSelectDropdown(props, ref) {
  var _classnames;

  var classes = props.classes,
      className = props.className,
      children = props.children,
      PopperProps = props.PopperProps;

  var _useDropdown = useDropdown(props, true),
      rootProps = _useDropdown.rootProps,
      buttonProps = _useDropdown.buttonProps,
      listContext = _useDropdown.listContext,
      listProps = _useDropdown.listProps;

  var contextList = useContext(CustomListContext);
  var ListComponent = isObjectEmpty(contextList) ? ListBase : contextList;

  var density = rootProps.density,
      disabled = rootProps.disabled,
      fullWidth = rootProps.fullWidth,
      isOpen = rootProps.isOpen,
      rootRef = rootProps.ref,
      restRootProps = _objectWithoutProperties(rootProps, ["density", "disabled", "fullWidth", "isOpen", "ref"]);

  return /*#__PURE__*/React.createElement("div", _assign({
    className: classnames(classes.root, classes["".concat(density, "Density")], (_classnames = {}, _defineProperty(_classnames, classes.disabled, disabled), _defineProperty(_classnames, classes.fullWidth, fullWidth), _classnames), className),
    ref: useForkRef(rootRef, ref)
  }, restRootProps), children ? children({
    DropdownButtonProps: buttonProps,
    isOpen: isOpen,
    itemToString: listProps.itemToString,
    selectedItem: listContext.state.selectedItem
  }) : /*#__PURE__*/React.createElement(DropdownButton, buttonProps), rootRef.current && /*#__PURE__*/React.createElement(Popper, _assign({
    anchorEl: rootRef.current,
    className: classes.popper,
    open: isOpen,
    placement: "bottom-start" // We don't want the default role of 'tooltip'
    ,
    role: null
  }, PopperProps), /*#__PURE__*/React.createElement(ListStateContext.Provider, {
    value: listContext
  }, /*#__PURE__*/React.createElement(ListComponent, _assign({
    classes: {
      root: classes.listRoot
    }
  }, listProps)))));
});
MultiSelectDropdown.defaultProps = {
  width: '180px',
  iconName: 'down-arrow'
};
process.env.NODE_ENV !== "production" ? MultiSelectDropdown.propTypes = {
  /**
   * Props to be applied on the default button component
   */
  // eslint-disable-next-line react/forbid-prop-types
  ButtonProps: PropTypes.object,

  /**
   * The component used for item instead of the default.
   */
  ListItem: PropTypes.func,

  /**
   * Properties applied to the `List` element.
   */
  // eslint-disable-next-line react/forbid-prop-types
  ListProps: PropTypes.object,

  /**
   * Override Popper props
   */
  // eslint-disable-next-line react/forbid-prop-types
  PopperProps: PropTypes.object,

  /**
   * Object that houses ADA-related props.
   *
   * @property {bool} virtualized Set to `true` to boost browser performance
   * for long lists by rendering only the items currently scrolled into view
   * (plus overscan items). JSX: `adaExceptions={{virtualized:true}}`
   * For better ADA support, omit (or set to `false`).
   */
  adaExceptions: PropTypes.shape({
    virtualized: PropTypes.bool
  }),

  /**
   * If the component has no border.
   */
  borderless: PropTypes.bool,

  /**
   * A ref to the button
   */
  buttonRef: refType,

  /**
   * Override the triggering component
   */
  children: PropTypes.func,

  /**
   * The className(s) of the component
   */
  className: PropTypes.string,

  /**
   * @external - material-ui
   * Useful to extend the style applied to components.
   */
  classes: PropTypes.objectOf(PropTypes.string),

  /**
   * The density of a component affects the style of the layout.
   * A high density component uses minimal sizing and spacing to convey the intended UI design.
   * Conversely, a low density component, maximizes the use of space and size to convey the UI Design.
   */
  density: PropTypes.oneOf(['touch', 'low', 'medium', 'high']),

  /**
   * Disable user interaction
   */
  disabled: PropTypes.bool,

  /**
   * The number of items displayed in the visible area.
   *
   * Note that this determines the max height of the list if the list height is not set to 100%.
   *
   * @default 10
   */
  displayedItemCount: PropTypes.number,

  /**
   * If `true`, the Dropdown will occupy the full width of it's container
   */
  fullWidth: PropTypes.bool,

  /**
   * Sets the icon name, default is 'down-arrow'
   */
  iconName: PropTypes.string,

  /**
   * Sets the size of the down arrow icon. If this is not specified, a default size based on density is used.
   */
  iconSize: PropTypes.number,

  /**
   * Sets the id of the component.
   */
  id: PropTypes.string,

  /**
   * This is the initial isOpen value.
   *
   * @default false
   */
  initialIsOpen: PropTypes.bool,

  /**
   * Pass an item that should be selected by default.
   */
  // eslint-disable-next-line react/forbid-prop-types
  initialSelectedItem: PropTypes.any,

  /**
   * Whether the menu should be considered open or closed. Some aspects of the
   * downshift component respond differently based on this value (for example,
   * if isOpen is true when the user hits "Enter" on the input field, then the
   * item at the highlightedIndex item is selected).
   */
  isOpen: PropTypes.bool,

  /**
   * Used to determine the string value for the selected item.
   */
  itemToString: PropTypes.func,

  /**
   * Customize width of the Dropdown List. This supersedes `width`.
   */
  listWidth: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),

  /**
   * @external - material-ui
   * Callback fired by the input component onBlur.
   *
   * @param {object} event The event source of the callback
   */
  onBlur: PropTypes.func,

  /**
   * Called when user clicks on the dropdown button.
   *
   * @param {object} event The event source of the callback
   */
  onButtonClick: PropTypes.func,

  /**
   * Called when the user selects an item and the selected item has changed.
   *
   * @param {object} event The event source of the callback
   * @param {any} selectedItem The item that was just selected
   */
  onChange: PropTypes.func,

  /**
   * @external - material-ui
   * Callback fired by the input component onFocus.
   *
   * @param {object} event The event source of the callback
   */
  onFocus: PropTypes.func,

  /**
   * Called when the user mouses off the button.
   */
  onMouseLeave: PropTypes.func,

  /**
   * Called when the user mouses over the button.
   */
  onMouseOver: PropTypes.func,

  /**
   * Called when the user selects an item, regardless of the previous selected
   * item.
   *
   * @param {object} event The event source of the callback
   * @param {any} selectedItem The item that was just selected
   */
  onSelect: PropTypes.func,

  /**
   * The currently selected item.
   */
  // eslint-disable-next-line react/forbid-prop-types
  selectedItem: PropTypes.any,

  /**
   * List of items when using a Dropdown.
   */
  source: PropTypes.arrayOf(PropTypes.any).isRequired,

  /**
   Customize width of Dropdown. Also controls Dropdown List if `listWidth` prop is not set.
   */
  width: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
} : void 0; // TODO (BREAKING CHANGE REQUIRED)
// https://issuetracking.jpmchase.net/jira12/browse/JPMUITK-2835
// Add unique JPMUI prefix to name to identify component as a Toolkit component

export default withStyles(style, {
  name: 'MultiSelectDropdown'
})(MultiSelectDropdown);
//# sourceMappingURL=MultiSelectDropdown.js.map